import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { apiPost } from '@/api/client';
import { tokenReceived } from '@/store/authSlice';
import type { AppDispatch } from '@/store/store';

/**
 * Magic-link landing page (`/auth/magic?token=<token>`).
 *
 * NOTE: Scribia's backend exposes magic-link verification as a POST
 * `/auth/verify-magic-link` that returns the access token in the body and sets the
 * HttpOnly refresh cookie — NOT Foundation's GET `/auth/magic` 302-redirect flow that
 * `useMagicLinkRedirect` assumes. So we verify via `apiPost` and apply the token here,
 * mirroring the email-code flow, rather than wiring `useMagicLinkRedirect`. On a missing
 * or invalid token we route to the auth error page.
 */
interface VerifyResponse {
  accessToken: string;
}

export default function MagicLinkPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [params] = useSearchParams();
  const token = params.get('token');
  const ranRef = useRef(false);

  useEffect(() => {
    // Guard React StrictMode's double-invoke (the token is single-use).
    if (ranRef.current) return;
    ranRef.current = true;

    if (!token) {
      navigate('/auth/error?code=invalid_link', { replace: true });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const result = await apiPost<VerifyResponse>('/auth/verify-magic-link', { token });
        if (cancelled) return;
        dispatch(tokenReceived({ accessToken: result.accessToken }));
        navigate('/documents', { replace: true });
      } catch {
        if (cancelled) return;
        navigate('/auth/error?code=invalid_link', { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, dispatch, navigate]);

  return (
    <div style={{ maxWidth: 480, margin: '4rem auto', textAlign: 'center' }}>
      <p>{t('auth.magic.verifying')}</p>
    </div>
  );
}
